import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransitionForm } from "../../../store/slices/transitionFormSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const cb = (v) => (v ? "☑" : "☐");
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const st = (v, target) => (v === target ? "☑" : "☐");

const ACTIVITIES = [
  { n: 1, label: "Supervised Outings (community, shopping, recreation)" },
  { n: 2, label: "Family Visits (on-site / therapeutic)" },
  { n: 3, label: "Home Visits (client returns home temporarily)" },
  { n: 4, label: "Overnight / Extended Home Leave" },
  { n: 5, label: "Vocational / Educational Site Visit" },
];

export default function TransitionFormPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentTransitionForm, isLoading, error } = useSelector((s) => s.transitionForm);

  useEffect(() => {
    if (id) dispatch(fetchTransitionForm(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentTransitionForm) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => { clearTimeout(t); window.onafterprint = null; };
    }
  }, [currentTransitionForm, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Transition Form print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentTransitionForm) return null;

  const d = currentTransitionForm;

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 16mm 14mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, table { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table { border-collapse: collapse !important; }
          table, th, td { border: 1px solid #000 !important; }
          th { background: #f0f0f0 !important; }
          .section-title { border: 1px solid #000 !important; background: #e9e9e9 !important; }
          .notes-box { border: 1px solid #000 !important; }
          .lbl { background: #f7f7f7 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .trans-print, .trans-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .trans-print { width: 100%; max-width: 800px; margin: 0 auto; padding: 18px; font-size: 10.5pt; line-height: 1.4; }
        .header-logo { text-align: center; margin-bottom: 6px; }
        .header-logo img { height: 62px; }
        .doc-title { text-align: center; font-size: 16pt; font-weight: bold; text-transform: uppercase; margin: 0 0 12px; }
        .section { margin-bottom: 14px; }
        .section-title { font-size: 11pt; font-weight: bold; background: #e9e9e9; border: 1px solid #000; padding: 4px 8px; text-transform: uppercase; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { border: 1px solid #000; padding: 5px 7px; font-size: 9.5pt; vertical-align: top; }
        th { background: #f0f0f0; text-align: left; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 24%; }
        .to-line { font-size: 11pt; margin-bottom: 10px; }
        .to-line span { margin-right: 18px; }
        .chk-list div { font-size: 10pt; padding: 2px 0; }
        .notes-box { border: 1px solid #000; min-height: 38px; padding: 5px 7px; white-space: pre-wrap; font-size: 9.5pt; margin-bottom: 6px; }
        .footer { text-align: center; font-size: 8.5pt; margin-top: 14px; border-top: 1px solid #000; padding-top: 6px; }
      `}</style>

      <PrintWrapper>
        <div className="trans-print">
          <div className="header-logo"><img src={logo} alt="The Recovery House Logo" /></div>
          <h1 className="doc-title">Transition &amp; Reintegration Process Form</h1>

          {/* Transition To */}
          <div className="section">
            <div className="to-line">
              <strong>Transition to:</strong>&nbsp;
            <span>{cb(d.to_day_rehab)} Day Rehab</span>
            <span>{cb(d.to_home_visit)} Home Visit</span>
            <span>{cb(d.to_other_facility)} Other Facility</span>
            <span>{cb(d.to_opd)} OPD</span>
            <span>{cb(d.to_others)} Others{d.others_specify ? ` (${d.others_specify})` : ""}</span>
          </div>
        </div>

        {/* A. Graduated Transition Activities */}
        <div className="section">
          <div className="section-title">A. Graduated Transition Activities</div>
          <table>
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Activity</th>
                <th>Initiated Date</th>
                <th>Frequency</th>
                <th>Accompanied By</th>
                <th style={{ width: "22%" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {ACTIVITIES.map(({ n, label }) => (
                <tr key={n}>
                  <td>{label}</td>
                  <td>{fmt(d[`act${n}_initiated_date`])}</td>
                  <td>{val(d[`act${n}_frequency`])}</td>
                  <td>{val(d[`act${n}_accompanied_by`])}</td>
                  <td style={{ fontSize: "8.6pt" }}>
                    {st(d[`act${n}_status`], "Pending")} Pending&nbsp;
                    {st(d[`act${n}_status`], "Active")} Active&nbsp;
                    {st(d[`act${n}_status`], "Complete")} Complete
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* B. Discharge Preparation */}
        <div className="section">
          <div className="section-title">B. Discharge Preparation</div>
          <div className="chk-list">
            <div>{cb(d.disch_criteria_reviewed)} Discharge criteria reviewed with client and family</div>
            <div>{cb(d.disch_relapse_prevention)} Relapse prevention plan completed (WRAP or equivalent)</div>
            <div>{cb(d.disch_community_support)} Community support services identified and linked</div>
            <div>{cb(d.disch_medication_plan)} Medication management plan confirmed</div>
            <div>{cb(d.disch_crisis_contacts)} Crisis contacts established</div>
            <div>{cb(d.disch_followup_appointments)} Outpatient/follow-up appointments scheduled</div>
            <div>{cb(d.disch_housing_confirmed)} Housing confirmed and safety-assessed</div>
            <div>{cb(d.disch_family_orientation)} Family orientation and briefing conducted</div>
          </div>

          <table style={{ marginTop: "8px" }}>
            <tbody>
              <tr><td className="lbl">Planned Discharge Date</td><td>{fmt(d.planned_discharge_date)}</td><td className="lbl">Reintegration Date</td><td>{fmt(d.reintegration_date)}</td></tr>
            </tbody>
          </table>

          <div style={{ fontWeight: "bold", fontSize: "9.5pt", margin: "6px 0 2px" }}>Reintegration Notes:</div>
          <div className="notes-box">{d.reintegration_notes || ""}</div>
        </div>

        <div className="footer">CONFIDENTIAL — For Authorised Clinical Use Only</div>
      </div>
      </PrintWrapper>
    </>
  );
}
